import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useInsforge } from '../provider/InsforgeProvider';
import {
  ProfileModalOverlay,
  ProfileModalContainer,
  ProfileModalHeader,
  ProfileModalTitle,
  ProfileModalCloseButton,
  ProfileModalBody,
  ProfileAvatarSection,
  ProfileAvatar,
  ProfileAvatarImage,
  ProfileFieldsContainer,
  ProfileField,
  ProfileFieldLabel,
  ProfileFieldValue,
  ProfileFieldInput,
  ProfileModalFooter,
  ProfileButton,
  ProfileSpinner,
} from './styled';

export interface UserProfileModalProps {
  /** Called when the modal is closed */
  onClose: () => void;
  /** Called when an error occurs */
  onError?: (error: string) => void;
}

/**
 * User profile modal component.
 * Displays user profile information with edit capability for name.
 */
export function UserProfileModal({ onClose, onError }: UserProfileModalProps) {
  const { user, updateUser, isLoaded } = useInsforge();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [name, setName] = useState('');

  // Initialize name when user changes
  useEffect(() => {
    if (user?.profile?.name) {
      setName(user.profile.name);
    }
  }, [user?.profile?.name]);

  // Validate avatar URL when it changes
  useEffect(() => {
    setImageError(false);

    const avatarUrl = user?.profile?.avatar_url;
    if (!avatarUrl) {
      return;
    }

    const checkImageUrl = async () => {
      try {
        const response = await fetch(avatarUrl, {
          method: 'HEAD',
          cache: 'no-cache',
        });
        if (!response.ok) {
          setImageError(true);
        }
      } catch {
        setImageError(true);
      }
    };

    void checkImageUrl();
  }, [user?.profile?.avatar_url]);

  const handleSave = useCallback(async () => {
    if (!user) return;

    setIsSaving(true);

    try {
      const result = await updateUser({ name });

      if (result?.error) {
        onError?.(result.error);
      } else {
        setIsEditing(false);
      }
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  }, [user, name, updateUser, onError]);

  const handleCancel = useCallback(() => {
    // Reset to current user value
    setName(user?.profile?.name || '');
    setIsEditing(false);
  }, [user?.profile?.name]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          handleCancel();
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isEditing, handleCancel, onClose]);

  if (!isLoaded || !user) {
    return null;
  }

  const initials = user.profile?.name
    ? user.profile.name.charAt(0).toUpperCase()
    : user.email.split('@')[0].slice(0, 2).toUpperCase();

  const modalContent = (
    <ProfileModalOverlay onClick={handleOverlayClick}>
      <ProfileModalContainer onClick={(e) => e.stopPropagation()}>
        <ProfileModalHeader>
          <ProfileModalTitle>Profile</ProfileModalTitle>
          <ProfileModalCloseButton onClick={onClose} aria-label="Close">
            <X />
          </ProfileModalCloseButton>
        </ProfileModalHeader>

        <ProfileModalBody>
          <ProfileAvatarSection>
            <ProfileAvatar>
              {user.profile?.avatar_url && !imageError ? (
                <ProfileAvatarImage
                  src={user.profile.avatar_url}
                  alt={user.profile?.name || user.email}
                  onError={() => setImageError(true)}
                />
              ) : (
                initials
              )}
            </ProfileAvatar>
          </ProfileAvatarSection>

          <ProfileFieldsContainer>
            {/* Email - always read-only */}
            <ProfileField>
              <ProfileFieldLabel>email</ProfileFieldLabel>
              <ProfileFieldValue>{user.email}</ProfileFieldValue>
            </ProfileField>

            {/* Name - editable */}
            <ProfileField>
              <ProfileFieldLabel>name</ProfileFieldLabel>
              {isEditing ? (
                <ProfileFieldInput
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSaving}
                  placeholder="Enter your name"
                />
              ) : (
                <ProfileFieldValue>{user.profile?.name || '-'}</ProfileFieldValue>
              )}
            </ProfileField>
          </ProfileFieldsContainer>
        </ProfileModalBody>

        <ProfileModalFooter>
          {isEditing ? (
            <>
              <ProfileButton onClick={handleCancel} disabled={isSaving}>
                Cancel
              </ProfileButton>
              <ProfileButton $primary onClick={() => void handleSave()} disabled={isSaving}>
                {isSaving && <ProfileSpinner />}
                {isSaving ? 'Saving...' : 'Save'}
              </ProfileButton>
            </>
          ) : (
            <ProfileButton $primary onClick={() => setIsEditing(true)}>
              Edit Profile
            </ProfileButton>
          )}
        </ProfileModalFooter>
      </ProfileModalContainer>
    </ProfileModalOverlay>
  );

  // Use portal to render modal outside of any containing block (e.g., elements with backdrop-filter)
  // This ensures position: fixed works correctly relative to the viewport
  return createPortal(modalContent, document.body);
}
